import axios from "axios";
import store from "../store/store";
import i18n from "./i18n";
import { POPUP_SESSION_KEY } from "./NoticePopupUtil";

const baseURL = process.env.REACT_APP_BASE_URL;

const api = axios.create({
  baseURL: baseURL,
  headers: {
    "Content-Type": "application/json",
  },
});

/**
 * 인증이 필요 없는 공개 API 전용 인스턴스.
 * 로그인 화면(비로그인 상태)에서 호출하는 팝업 공지 조회·다운로드에 사용한다.
 * api 인스턴스의 응답 인터셉터는 오류 시 /login·/error로 이동시키므로,
 * 팝업 조회 실패가 화면 이동을 유발하지 않도록 인터셉터를 두지 않는다.
 */
export const publicApi = axios.create({
  baseURL: baseURL,
  headers: {
    "Content-Type": "application/json",
  },
});

// request interceptor
api.interceptors.request.use((config) => {
  if (config.data && typeof config.data === "object") {
    config.data.lang = i18n.language.toUpperCase();
  }

  const userInfo = JSON.parse(sessionStorage.getItem("userInfo"));
  const menuAuthInfo = store.getState().menuAuth.menuAuthInfo;

  if (
    userInfo?.grantType &&
    userInfo?.accessToken &&
    !config.url.includes("/user/reIssueAccessToken")
  ) {
    config.headers.Authorization = `${userInfo.grantType} ${userInfo.accessToken}`;
  }

  if (menuAuthInfo && Array.isArray(menuAuthInfo)) {
    const info = menuAuthInfo.find(
      (menuAuth) =>
        menuAuth.url && window.location.pathname.startsWith(menuAuth.url)
    );
    if (info) {
      config.headers["X-menu-id"] = info.id;
    }
  }

  return config;
});

let isRefreshing = false;
let apiCallPendingList = [];

const runPendingApiCalls = () => {
  apiCallPendingList.forEach((apiCall) => {
    apiCall();
  });
  apiCallPendingList = [];
};

// response interceptor
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { status, data } = error.response || {};
    const originalRequest = error.config;

    // 권한 없음
    if (status === 403) {
      logout();
      return Promise.resolve();
    }

    // refresh token 만료
    if (status !== 200 && originalRequest.url === "/user/reIssueAccessToken") {
      logout();
      return Promise.resolve();
    }

    // 토큰 재발급 시도
    if (status === 401) {
      return handleTokenRefresh(originalRequest);
    }

    // 잘못된 접근 & 서버 에러
    if (isInvalidRequest(status, data)) {
      errorPage();
      return Promise.resolve();
    }

    return Promise.reject(error);
  }
);

const handleTokenRefresh = async (originalRequest) => {
  if (isRefreshing) {
    return new Promise((resolve) => {
      apiCallPendingList.push(() => {
        resolve(api(originalRequest));
      });
    });
  }
  isRefreshing = true;

  try {
    const userInfo = JSON.parse(sessionStorage.getItem("userInfo"));
    const response = await api.post(
      "/user/reIssueAccessToken",
      {},
      {
        headers: {
          refreshToken: userInfo?.refreshToken,
        },
      }
    );
    const newUserInfo = response?.data;

    userInfo.accessToken = newUserInfo.accessToken;
    userInfo.refreshToken = newUserInfo.refreshToken;

    // sessionStorage를 비우므로 세션 내 팝업 공지 노출 억제 상태는 유지한다.
    const closedPopupIds = sessionStorage.getItem(POPUP_SESSION_KEY);

    sessionStorage.clear();
    sessionStorage.setItem("userInfo", JSON.stringify(userInfo));

    if (closedPopupIds) {
      sessionStorage.setItem(POPUP_SESSION_KEY, closedPopupIds);
    }

    runPendingApiCalls();
    return api(originalRequest);
  } finally {
    isRefreshing = false;
  }
};

const isInvalidRequest = (status, data) => {
  return status === 400 && (!data || data?.result === 999);
};

function logout() {
  sessionStorage.clear();
  window.location.href = "/login";
}

function errorPage() {
  sessionStorage.clear();
  window.location.href = "/error";
}

export default api;
